import {
	getAllMockInvoices,
	getMockInvoiceById,
	MOCK_PAID_INVOICES,
	MOCK_PARTIAL_INVOICES,
} from "./mockPatientAccountsData";

const delay = (ms = 400) => new Promise((resolve) => setTimeout(resolve, ms));

const matchesSearch = (invoice, search) => {
	if (!search?.trim()) return true;
	const term = search.trim().toLowerCase();
	return (
		invoice.invoice_no?.toLowerCase().includes(term) ||
		invoice.patient?.full_name?.toLowerCase().includes(term) ||
		invoice.file_no?.toLowerCase().includes(term) ||
		invoice.patient?.health_id?.toLowerCase().includes(term)
	);
};

const matchesDateRange = (invoice, fromDate, toDate) => {
	const invoiceDate = (invoice.invoice_date || invoice.createdAt || "").split("T")[0];
	if (fromDate && invoiceDate < fromDate) return false;
	if (toDate && invoiceDate > toDate) return false;
	return true;
};

const matchesType = (invoice, invoiceType) => {
	if (!invoiceType) return true;
	const type = invoice.invoice_type || invoice.type;
	if (invoiceType === "other_services") {
		return type === "other_services" || type === "treatment_package";
	}
	return type === invoiceType;
};

const filterInvoices = (source, filters) => {
	const { patientId, fromDate, toDate, invoiceType, search } = filters;
	return source.filter((invoice) => {
		if (patientId && invoice.patient_id !== patientId) return false;
		if (!matchesDateRange(invoice, fromDate, toDate)) return false;
		if (!matchesType(invoice, invoiceType)) return false;
		if (!matchesSearch(invoice, search)) return false;
		return true;
	});
};

export const mockGetPatientInvoices = async (body) => {
	await delay();
	const source = body.paymentStatus === "paid" ? MOCK_PAID_INVOICES : MOCK_PARTIAL_INVOICES;
	const filtered = filterInvoices(source, body);
	const page = body.page || 1;
	const limit = body.limit || 10;
	const start = (page - 1) * limit;
	const paginated = filtered.slice(start, start + limit);

	return {
		data: {
			status: "success",
			data: {
				invoices: paginated,
				total: filtered.length,
				count: filtered.length,
			},
		},
	};
};

export const mockGetPatientInvoiceDetails = async (body) => {
	await delay();
	const invoice = getMockInvoiceById(body.invoiceId);
	if (!invoice) {
		return {
			data: {
				status: "error",
				message: "Invoice not found",
			},
		};
	}
	return {
		data: {
			status: "success",
			data: {
				invoice_details: { ...invoice },
			},
		},
	};
};

export const mockCollectPatientInvoicePayment = async (body) => {
	await delay(600);
	const invoice = getMockInvoiceById(body.invoiceId);
	if (!invoice) {
		return { data: { status: "error", message: "Invoice not found" } };
	}

	const paidAmount = Number(body.paidAmount || 0);
	const newPaid = Number(invoice.paid_amount || 0) + paidAmount;
	const pending = Math.max(0, Number(invoice.invoice_amount || 0) - newPaid);
	const isPaid = body.markAsPaid || pending <= 0;

	invoice.paid_amount = isPaid ? invoice.invoice_amount : newPaid;
	invoice.pending_amount = isPaid ? 0 : pending;
	invoice.due_amount = invoice.pending_amount;
	invoice.payment_status = isPaid ? "paid" : newPaid > 0 ? "partially_paid" : "unpaid";

	invoice.payment_history = [
		...(invoice.payment_history || []),
		{
			payment_date: new Date().toISOString(),
			paid_amount: paidAmount,
			payment_mode: body.paymentMode,
		},
	];

	if (invoice.financial_summary) {
		invoice.financial_summary.total_paid = invoice.paid_amount;
		invoice.financial_summary.due = invoice.pending_amount;
	}

	if (isPaid && invoice.payment_status === "paid") {
		const partialIndex = MOCK_PARTIAL_INVOICES.findIndex((i) => i._id === invoice._id);
		if (partialIndex !== -1) {
			MOCK_PARTIAL_INVOICES.splice(partialIndex, 1);
			MOCK_PAID_INVOICES.unshift({ ...invoice });
		}
	}

	return {
		data: {
			status: "success",
			message: isPaid ? "Payment collected and invoice marked as paid." : "Partial payment collected successfully.",
		},
	};
};

export const getMockInvoicesSnapshot = () => getAllMockInvoices();
